import { useState, useEffect, useRef, useContext } from 'react';
import CreditNoteScroll from './CreditNoteScroll';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import GeneralModal from '../../helpers/GeneralModal';
import { createModal } from 'react-modal-promise';
import LogicContext from '../../Context/LogicContext';
import { SetEditCreditNoteAtom, GetCreditNoteListAtom, SetCreditNoteListAtom, CreditNoteState } from '../../store/CreditNoteStore';
import { ImAttachment } from 'react-icons/im';
import { FaFilePdf } from 'react-icons/fa';
import { MdDelete, MdEdit } from 'react-icons/md';
import { IoPrintSharp } from 'react-icons/io5';
import { creditNoteService } from '../../services/creditNoteService';
import CreditNoteDetailsContent from './CreditNoteDetailsContent';
import { useReactToPrint } from 'react-to-print';
import CreditNoteFilter from './CreditNoteFilter';
import CreditNoteOptions from './CreditNoteOptions';
import { CreditNote, CreditNoteDetails, AccountInfo } from './interface/CreditNote.Interface';
import { toLowercaseKeys } from '../../helpers/toLowerCaseKeys';

// Types

interface StatusInfo {
  [key: string]: any;
}

interface StatusSelection {
  credit_note_status_id: number;
  credit_note_status_name: string;
}

interface ModalResponse {
  decision: boolean;
}

interface ApiResponse {
  data: CreditNoteDetails[] | CreditNoteState;
}

interface StatusChangeResponse {
  data: Array<{
    message_id: number;
    message_text: string;
  }>;
}

interface LogicContextType {
  showToast: (message: string, type: string) => void;
}

function CreditNoteDetailsPage() {
  const myPromiseModal = createModal(GeneralModal);
  const setCreditNoteEdit = SetEditCreditNoteAtom();
  const getCreditNoteListAtom: CreditNoteState = GetCreditNoteListAtom();
  const setCreditNoteListAtom = SetCreditNoteListAtom();

  const logicContext = useContext(LogicContext) as LogicContextType;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const componentRef = useRef<HTMLDivElement>(null);
  const url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];
  const [creditNoteDetails, setCreditNoteDetails] = useState<CreditNoteDetails>({} as CreditNoteDetails);
  const [statusInfo, setStatusInfo] = useState<StatusInfo[]>([] as StatusInfo[]);
  const [accountInfo, setAccountInfo] = useState<AccountInfo[]>([]);

  useEffect(() => {
    let isMounted = true;
    if (id) {
      loadDetails(id, isMounted);
    }
    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    document.title = 'Credit note page';
    if (!getCreditNoteListAtom.credit_note?.length) {
      loadTableList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCreditNoteListAtom.credit_note]);

  const loadTableList = (): void => {
    creditNoteService.creditNoteList().then((res: ApiResponse) => {
      if (res.data) {
        setCreditNoteListAtom(res.data as CreditNoteState);
      }
    });
  };

  const loadDetails = async (noteId: string, isMounted: boolean = true): Promise<void> => {
    try {
      const response: ApiResponse = await creditNoteService.creditNoteDetails(noteId);
      const serverData = toLowercaseKeys(response.data);
      if (Array.isArray(serverData?.credit_note) && (serverData?.credit_note as CreditNoteDetails[]).length) {
        const creditNote = (serverData?.credit_note as CreditNoteDetails[])[0];
        const tmpStatusInfo = serverData?.status_info;
        const tmpAccountInfo = serverData?.account_info || [];
        if (isMounted) {
          setCreditNoteDetails(creditNote);
          setStatusInfo(tmpStatusInfo?.length ? tmpStatusInfo : []);
          setAccountInfo(tmpAccountInfo);
        }
        const newList = getCreditNoteListAtom?.credit_note.map((item: any) => {
          if (item.ref_no === creditNote.ref_no) {
            return { ...item, ...creditNote };
          } else {
            return item;
          }
        });
        setCreditNoteListAtom({
          credit_note: newList,
          status_info: getCreditNoteListAtom.status_info,
        });
      }
    } catch (error) {
      console.log('Error ', error);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const handleDownloadPdf = async (): Promise<void> => {
    if (!id) return;
    const result = (await myPromiseModal({
      title: 'Download PDF',
      body: `Do you want to download ${creditNoteDetails?.credit_note_number || 'this credit note'} as PDF?`,
      showInput: false,
      placeHolder: '',
    })) as ModalResponse;
    if (result.decision !== true) return;

    try {
      const response = await creditNoteService.downloadCreditNotePdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CreditNote-${creditNoteDetails?.credit_note_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      logicContext.showToast('Unable to download PDF', 'error');
    }
  };

  const addNewRecord = (): void => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
  };

  const viewCustomerDetails = (customerId: string): void => {
    const customerRoute = `/${currModuleId}/${currModuleName}/customers/customer-details/${customerId}`;
    // Option A: Open in new tab using window.open()
    window.open(customerRoute, '_blank');
  };

  const editCreditNoteOrder = async (): Promise<void> => {
    const response = (await myPromiseModal({
      title: 'Modify Credit Note',
      body: ' Do you want to modify this record?',
      showInput: false,
      placeHolder: '',
    })) as ModalResponse;

    if (response.decision === true) {
      setCreditNoteEdit(creditNoteDetails);
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
    }
  };

  const onStatusChange = async (selection: StatusSelection): Promise<void> => {
    if (!id) return;


    const formData = {
      ref_no: id,
      option: selection?.credit_note_status_id,
    };
    const response = (await myPromiseModal({
      title: `Change status to ${selection?.credit_note_status_name}`,
      body: ' Do you want to modify this record?',
      showInput: false,
      placeHolder: '',
    })) as ModalResponse;

    if (response?.decision === true) {
      creditNoteService
        .creditNoteStatus(formData).then((res: any) => {
          loadDetails(id);
          const responseData = res.data;
          if (responseData !== undefined) {
            const { message_text: Message_text, message_id } = responseData?.[0] || res.data?.result_status?.[0];
            if (message_id == 1) {
              logicContext.showToast(Message_text, 'success');
            } else {
              logicContext.showToast(Message_text || 'Action failed', 'error');
            }
          }
        })
        .catch(err => {
          console.error('Error changing status:', err);
        });
    }
  };

  return (
    <>
      <section className="">
        <section className="flex flex-wrap justify-between">
          <div className="mt-3">
            {/* {selectedList.length > 0 && (
              <Dropdown>
                <Dropdown.Toggle
                  className="border-[1px] border-gray-200"
                  style={{
                    backgroundColor: "#FFFFFF",
                    color: "#45444B",
                    fontSize: "12px",
                    height: "40px",
                  }}
                  id="dropdown-basic"
                >
                  Bulk Actions
                </Dropdown.Toggle>

                <Dropdown.Menu className="w-32 px-2 text-sm shadow-lg">
                  {getBulkActions?.map((action, i) => {
                    return (
                      <Dropdown.Item
                        className="px-0 text-gray-600"
                        key={i}
                        onClick={() => bulkActions(action)}
                      >
                        {action.Action_Name}
                      </Dropdown.Item>
                    );
                  })}
                </Dropdown.Menu>
              </Dropdown>
            )} */}
          </div>

          <div className="flex flex-row flex-wrap items-center">
            <div className="mt-5 flex flex-row items-center mr-6">
              <button onClick={addNewRecord} className="btn btn-primary m-4">
                Add New
              </button>
              {statusInfo?.length > 0 && <CreditNoteOptions bulkActionData={statusInfo} onFilterSelect={onStatusChange} />}

            </div>
            <div className="mt-3 flex fle-row items-center space-x-1 ">
              <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
                <ImAttachment className="h-[20px] w-[20px] flex-shrink-0 text-gray-600" />
              </div>

              <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
                <FaFilePdf onClick={handleDownloadPdf} className="h-[20px] w-[20px] flex-shrink-0 text-gray-600" />
              </div>

              <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
                <IoPrintSharp onClick={handlePrint} className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
              </div>
              <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
                <MdEdit onClick={editCreditNoteOrder} className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
              </div>
              {/* <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
                <MdDelete className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
              </div> */}
            </div>
          </div>
        </section>
        <section className="mt-4 lg:flex space-x-1">
          <div className="lg:w-[210px]">
            <CreditNoteScroll></CreditNoteScroll>
          </div>
          <section className="w-full overflow-x-hidden mt-4 lg:mt-0 lg:pl-4 ">
            {/* Paste here */}

            <CreditNoteDetailsContent
              viewCustomerDetails={viewCustomerDetails}
              ref={componentRef}
              id={id || ''}
              creditNoteDetails={creditNoteDetails}
              accountInfo={accountInfo}
            />
            {/* Paste end */}
          </section>
        </section>
      </section>
    </>
  );
}

export default CreditNoteDetailsPage;
